import React, {
  useCallback, useEffect, useMemo, useState, Fragment, ReactElement,
} from 'react';
import { connect, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router';
import ContentHeader from '../../components/content-header/content-header.component';
import PageTabs from '../../components/page-tabs/page-tabs.component';
import Loader from '../../components/loader/loader.component';
import CheckAccess from '../../components/check-access/check-access.component';
import PullToRefresh from '../../components/pull-to-refresh/pull-to-refresh.component';
import NoContentBlock from '../../components/no-content-block/no-content-block.component';
import FilteredLink from '../../components/filtered-link/filtered-link.component';
import Image from '../../components/image/image.component';
import ChipLink from '../../components/chip-link/chip-link.component';
import Button from '../../components/button/button.component';
import FilterPanel from '../../components/filter-panel/filter-panel.component';
import Controls from '../app/controls/controls.component';
import SearchBar from '../app/search-bar/search-bar.component';
import SearchResultGroup from './search-result-group/search-result-group.component';
import SearchFilters from './search-filters/search-filters.component';
import FormatService from '../../services/format-service';
import useScreenMobile from '../../hooks/use-screen-mobile';
import { getSearchResultData, resetSearchResultData } from './search.action';
import { FILTER_VALUE_EMPTY } from '../../constants/system/filter-values';
import { TAB_INDEX_DEFAULT } from '../../constants/system/tab-indexes';
import {
  SEARCH_RESULT_VIEW, ACTIVITY_LIST_VIEW, ASSET_LIST_VIEW, CONTACT_LIST_VIEW, DEAL_LIST_VIEW, DEAL_TASK_LIST_VIEW,
} from '../../constants/entity/permissions';
import {
  ROUTE_CONTACT, ROUTE_DEAL, ROUTE_ASSET, ROUTE_ACTIVITY, ROUTE_SEARCH,
} from '../../constants/system/routes';
import {
  QUERY_PARAM_QUERY, QUERY_PARAM_TAG_ID, QUERY_PARAM_DEAL_TYPE, QUERY_PARAM_LOCATION, QUERY_PARAM_ASSET_TYPE,
  QUERY_PARAM_DEAL_SIZE, QUERY_PARAM_TAB, QUERY_PARAM_DEAL_TASK_ID,
} from '../../constants/system/query-params';
import { SORT_TYPE_ASC } from '../../constants/system/sort-types';
import { FILTER_KEY_NAME } from '../../constants/system/filter-keys';
import { TAB_NAME_DUE_DILIGENCE } from '../../constants/system/tab-names';
import {
  FILTER_TYPE_ASSET_TYPE, FILTER_TYPE_DEAL_SIZE, FILTER_TYPE_DEAL_TYPE, FILTER_TYPE_LOCATION,
} from '../../constants/system/filter-types';
import {
  SEARCH_RESULT_GROUP_PEOPLE, SEARCH_RESULT_GROUP_COMPANIES, SEARCH_RESULT_GROUP_DEALS,
  SEARCH_RESULT_GROUP_DEALS_CLOSED, SEARCH_RESULT_GROUP_ASSETS, SEARCH_RESULT_GROUP_ACTIVITIES,
  SEARCH_RESULT_GROUP_DEAL_TASKS,
} from '../../constants/system/search-result-groups';
import AssetDefaultImage from '../../static/images/asset-default-image.png';
import CompanyDefaultImage from '../../static/images/company-default-image.png';
import PersonDefaultImage from '../../static/images/person-default-image.png';

const Search: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const SEARCH_GROUP_KEY_CONTACT_PERSON_LIST: SearchResultGroupListType = 'contactPersonList';
  const SEARCH_GROUP_KEY_CONTACT_COMPANY_LIST: SearchResultGroupListType = 'contactCompanyList';
  const SEARCH_GROUP_KEY_DEAL_LIST: SearchResultGroupListType = 'dealList';
  const SEARCH_GROUP_KEY_DEAL_CLOSED_LIST: SearchResultGroupListType = 'dealClosedList';
  const SEARCH_GROUP_KEY_ASSET_LIST: SearchResultGroupListType = 'assetList';
  const SEARCH_GROUP_KEY_ACTIVITY_LIST: SearchResultGroupListType = 'activityList';
  const SEARCH_GROUP_KEY_DEAL_TASK_LIST: SearchResultGroupListType = 'dealTaskList';

  const {
    isDataLoaded,
    isRightDrawerOpen,
    searchTag,
    searchResultData,
    filterDealTypeList,
    filterLocationList,
    filterAssetTypeList,
    filterDealSizeList,
  } = props;

  const navigate: ReactRouterNavigate = useNavigate();
  const location: ReactRouterLocation = useLocation();
  const dispatch: ReduxDispatch = useDispatch();
  const isMobileScreen: boolean = useScreenMobile();

  const locationQueryParams: ParsedQueryParams | null = FormatService.parseQueryParams(location.search);
  const queryTab: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_TAB]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_TAB].toString()) : null;
  const queryParamQuery: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_QUERY]
    ? FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_QUERY].toString()) : null;
  const queryFilterDealType: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_DEAL_TYPE]
    && FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_DEAL_TYPE].toString());
  const queryFilterLocation: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_LOCATION]
    && FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_LOCATION].toString());
  const queryFilterAssetType: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_ASSET_TYPE]
    && FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_ASSET_TYPE].toString());
  const queryFilterDealSize: string | null = locationQueryParams && locationQueryParams[QUERY_PARAM_DEAL_SIZE]
    && FormatService.parseStringParam(locationQueryParams[QUERY_PARAM_DEAL_SIZE].toString());
  const tagId: number | null = locationQueryParams && locationQueryParams[QUERY_PARAM_TAG_ID]
    ? FormatService.parseInt(locationQueryParams[QUERY_PARAM_TAG_ID].toString()) : null;

  const DEFAULT_SORT_CONTACT_LIST: SortParams = useMemo((): SortParams => ({
    field: 'name',
    type: SORT_TYPE_ASC,
    isNumber: false,
  }), []);

  const DEFAULT_SORT_ASSET_LIST: SortParams = useMemo((): SortParams => ({
    field: 'name',
    type: SORT_TYPE_ASC,
    isNumber: false,
  }), []);

  const DEFAULT_SORT_DEAL_LIST: SortParams = useMemo((): SortParams => ({
    field: 'name',
    type: SORT_TYPE_ASC,
    isNumber: false,
  }), []);

  const DEFAULT_SORT_ACTIVITY_LIST: SortParams = useMemo((): SortParams => ({
    field: 'dealName',
    type: SORT_TYPE_ASC,
    isNumber: false,
  }), []);

  const DEFAULT_SORT_DEAL_TASK_LIST: SortParams = useMemo((): SortParams => ({
    field: 'name',
    type: SORT_TYPE_ASC,
    isNumber: false,
  }), []);

  const DEFAULT_FILTERS: Record<string, FilterValue> = useMemo((): Record<string, FilterValue> => ({
    [FILTER_TYPE_DEAL_TYPE]: null,
    [FILTER_TYPE_LOCATION]: null,
    [FILTER_TYPE_ASSET_TYPE]: null,
    [FILTER_TYPE_DEAL_SIZE]: null,
  }), []);

  const FILTER_OPTION_MAP: Record<string, FilterOption[]> = useMemo((): Record<string, FilterOption[]> => ({
    [FILTER_TYPE_DEAL_TYPE]: filterDealTypeList,
    [FILTER_TYPE_LOCATION]: filterLocationList,
    [FILTER_TYPE_ASSET_TYPE]: filterAssetTypeList,
    [FILTER_TYPE_DEAL_SIZE]: filterDealSizeList,
  }), [filterDealTypeList, filterLocationList, filterAssetTypeList, filterDealSizeList]);

  const FILTER_KEY_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [FILTER_TYPE_DEAL_TYPE]: FILTER_KEY_NAME,
    [FILTER_TYPE_LOCATION]: FILTER_KEY_NAME,
    [FILTER_TYPE_ASSET_TYPE]: FILTER_KEY_NAME,
    [FILTER_TYPE_DEAL_SIZE]: FILTER_KEY_NAME,
  }), []);

  const FILTER_LABEL_MAP: Record<string, string> = useMemo((): Record<string, string> => ({
    [FILTER_TYPE_DEAL_TYPE]: 'Deal Type',
    [FILTER_TYPE_LOCATION]: 'Location',
    [FILTER_TYPE_ASSET_TYPE]: 'Asset Class',
    [FILTER_TYPE_DEAL_SIZE]: 'Deal Size',
  }), []);

  const FILTER_LABEL_KEY_MAP: Record<string, string> = useMemo((): Record<string, string> => ({}), []);

  const SEARCH_RESULT_GROUP_KEY_MAP: Record<string, SearchResultGroupListType>
    = useMemo((): Record<string, SearchResultGroupListType> => ({
    [SEARCH_RESULT_GROUP_PEOPLE]: SEARCH_GROUP_KEY_CONTACT_PERSON_LIST,
    [SEARCH_RESULT_GROUP_COMPANIES]: SEARCH_GROUP_KEY_CONTACT_COMPANY_LIST,
    [SEARCH_RESULT_GROUP_DEALS]: SEARCH_GROUP_KEY_DEAL_LIST,
    [SEARCH_RESULT_GROUP_DEALS_CLOSED]: SEARCH_GROUP_KEY_DEAL_CLOSED_LIST,
    [SEARCH_RESULT_GROUP_ASSETS]: SEARCH_GROUP_KEY_ASSET_LIST,
    [SEARCH_RESULT_GROUP_ACTIVITIES]: SEARCH_GROUP_KEY_ACTIVITY_LIST,
    [SEARCH_RESULT_GROUP_DEAL_TASKS]: SEARCH_GROUP_KEY_DEAL_TASK_LIST,
  }), []);

  const [filters, setFilters]: [Record<string, FilterValue>, Function] = useState(DEFAULT_FILTERS);
  const [currentFilters, setCurrentFilters]: [Record<string, FilterValue>, Function] = useState(DEFAULT_FILTERS);
  const [searchResultListData, setSearchResultListData]: [Record<string, SearchResultGroupItem[]>, Function]
    = useState({});
  const [searchResultTotalData, setSearchResultTotalData]: [Record<string, number>, Function] = useState({});
  const [tabIndex, setTabIndex]: [number | null, Function] = useState(null);
  const [companySubItemsExpandedMap, setCompanySubItemsExpandedMap]: [Record<string, boolean> | null, Function]
    = useState({});

  const contactListViewPermissions: string[] = useMemo((): string[] => [CONTACT_LIST_VIEW], []);
  const dealListViewPermissions: string[] = useMemo((): string[] => [DEAL_LIST_VIEW], []);
  const assetListViewPermissions: string[] = useMemo((): string[] => [ASSET_LIST_VIEW], []);
  const activityListViewPermissions: string[] = useMemo((): string[] => [ACTIVITY_LIST_VIEW], []);
  const dealTaskListViewPermissions: string[] = useMemo((): string[] => [DEAL_TASK_LIST_VIEW], []);
  const searchResultViewPermissions: string[] = useMemo((): string[] => [SEARCH_RESULT_VIEW], []);

  const searchCriteria: string | null = searchTag ? searchTag.name : queryParamQuery;
  const pageTitle: string = searchCriteria ? `Search results${searchTag ? ` by tag` : ''}: ${searchCriteria}`
    : 'Search';
  const withAllFiltersEmpty: boolean = !filters[FILTER_TYPE_DEAL_TYPE] && !filters[FILTER_TYPE_LOCATION]
    && !filters[FILTER_TYPE_ASSET_TYPE] && !filters[FILTER_TYPE_DEAL_SIZE];

  const onShowCompanyStaffClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, companyId: number, isLoaded: boolean,
  ): void => {
    if (!isLoaded) {
      dispatch(getSearchResultData({
        companyId,
      }));
    }

    setCompanySubItemsExpandedMap({
      ...companySubItemsExpandedMap,
      [companyId]: !companySubItemsExpandedMap[companyId],
    });

    event.stopPropagation();
  }, [companySubItemsExpandedMap]);

  const tableSearchResultPersonColumnList: SearchColumnItem[] = useMemo((): SearchColumnItem[] => [
    {
      id: 1,
      field: 'photo',
      label: 'Photo',
      isSortable: false,
      isNumber: false,
      isImage: true,
      component: (searchResultItem: SearchResultItem): ReactElement => (
        <Image
          isCentered
          text={searchResultItem.name}
          link={searchResultItem.photo || PersonDefaultImage}
        />
      ),
    },
    {
      id: 2,
      field: 'name',
      label: 'Name',
      isSortable: true,
      component: (searchResultItem: SearchResultItem): ReactElement => (
        <FilteredLink withNewWindow link={`${ROUTE_CONTACT}/${searchResultItem.id}`}>
          {searchResultItem.name}
        </FilteredLink>
      ),
    },
    {
      id: 3,
      field: 'groupText',
      label: 'Group',
      isSortable: true,
    },
    {
      id: 4,
      field: 'location',
      label: 'Location',
      isSortable: false,
    },
    {
      id: 5,
      field: 'companyText',
      label: 'Company',
      isSortable: true,
    },
    {
      id: 6,
      field: 'tags',
      label: 'Tags',
      isSortable: false,
      isMultiline: true,
      component: (searchResultItem: SearchResultItem): ReactElement => (
        searchResultItem.tags.map((tagItem: Tag): ReactElement => (
          <ChipLink
            key={tagItem.id}
            label={tagItem.name}
            permission={SEARCH_RESULT_VIEW}
            onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
              onTagClick(event, tagItem.id);
            }}
          />
        ))
      ),
    },
  ], []);

  const tableSearchResultPersonColumnSubItemList: SearchColumnItem[] = useMemo((): SearchColumnItem[] => [
    {
      id: 1,
      field: 'photo',
      label: 'Photo',
      isSortable: false,
      isNumber: false,
      isImage: true,
      component: (searchResultItem: SearchResultItem): ReactElement => (
        <Image
          isCentered
          text={searchResultItem.name}
          link={searchResultItem.photo || PersonDefaultImage}
        />
      ),
    },
    {
      id: 2,
      field: 'name',
      label: 'Name',
      isSortable: true,
      component: (searchResultItem: SearchResultItem): ReactElement => (
        <FilteredLink withNewWindow link={`${ROUTE_CONTACT}/${searchResultItem.id}`}>
          {searchResultItem.name}
        </FilteredLink>
      ),
    },
    {
      id: 3,
      field: 'groupText',
      label: 'Group',
      isSortable: true,
    },
    {
      id: 4,
      field: 'location',
      label: 'Location',
      isSortable: false,
    },
    {
      id: 5,
      field: 'tags',
      label: 'Tags',
      isSortable: false,
      isMultiline: true,
      component: (searchResultItem: SearchResultItem): ReactElement => (
        searchResultItem.tags.map((tagItem: Tag): ReactElement => (
          <ChipLink
            key={tagItem.id}
            label={tagItem.name}
            permission={SEARCH_RESULT_VIEW}
            onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
              onTagClick(event, tagItem.id);
            }}
          />
        ))
      ),
    },
    {
      id: 6,
      label: '',
      isSortable: false,
    },
  ], []);

  const tableSearchResultCompanyColumnList: SearchColumnItem[] = useMemo((): SearchColumnItem[] => [
    {
      id: 1,
      field: 'photo',
      label: 'Photo',
      isSortable: false,
      isNumber: false,
      isImage: true,
      component: (searchResultItem: SearchResultItem): ReactElement => (
        <Image
          isCentered
          text={searchResultItem.name}
          link={searchResultItem.photo || CompanyDefaultImage}
        />
      ),
    },
    {
      id: 2,
      field: 'name',
      label: 'Name',
      isSortable: true,
      component: (searchResultItem: SearchResultItem): ReactElement => (
        <FilteredLink withNewWindow link={`${ROUTE_CONTACT}/${searchResultItem.id}`}>
          {searchResultItem.name}
        </FilteredLink>
      ),
    },
    {
      id: 3,
      field: 'groupText',
      label: 'Group',
      isSortable: true,
    },
    {
      id: 4,
      field: 'location',
      label: 'Location',
      isSortable: false,
    },
    {
      id: 5,
      field: 'tags',
      label: 'Tags',
      isSortable: false,
      isMultiline: true,
      component: (searchResultItem: SearchResultItem): ReactElement => (
        searchResultItem.tags.map((tagItem: Tag): ReactElement => (
          <ChipLink
            key={tagItem.id}
            label={tagItem.name}
            permission={SEARCH_RESULT_VIEW}
            onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
              onTagClick(event, tagItem.id);
            }}
          />
        ))
      ),
    },
    {
      id: 6,
      label: 'Company Staff',
      isSortable: false,
      component: (searchResultItem: SearchResultItem): ReactElement | boolean => (
        !!searchResultItem.relatedContactsTotal && (
          <Button
            isActive
            isText
            isExtended
            onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
              onShowCompanyStaffClick(event, searchResultItem.id, !!searchResultItem.subItems);
            }}
          >
            {companySubItemsExpandedMap && companySubItemsExpandedMap[searchResultItem.id] ? 'Hide' : 'Show'}
          </Button>
        )
      ),
    },
  ], [companySubItemsExpandedMap]);

  const tableSearchResultDealColumnList: SearchColumnItem[] = useMemo((): SearchColumnItem[] => [
    {
      id: 1,
      field: 'mainPhoto',
      label: 'Photo',
      isSortable: false,
      isNumber: false,
      isImage: true,
      component: (searchResultItem: SearchResultItem): ReactElement => (
        <Image
          isCentered
          text={searchResultItem.name}
          link={searchResultItem.mainPhoto || AssetDefaultImage}
        />
      ),
    },
    {
      id: 2,
      field: 'name',
      label: 'Name',
      isSortable: true,
      isNumber: false,
      component: (searchResultItem: SearchResultItem): ReactElement => (
        <FilteredLink withNewWindow link={`${ROUTE_DEAL}/${searchResultItem.id}`}>
          {searchResultItem.name}
        </FilteredLink>
      ),
    },
    {
      id: 3,
      field: 'statusText',
      label: 'Status',
      isSortable: true,
      isNumber: false,
    },
    {
      id: 4,
      field: 'location',
      label: 'Address',
      isSortable: false,
      isNumber: false,
    },
    {
      id: 5,
      field: 'tags',
      label: 'Tags',
      isSortable: false,
      isMultiline: true,
      component: (searchResultItem: SearchResultItem): ReactElement => (
        searchResultItem.tags.map((tagItem: Tag): ReactElement => (
          <ChipLink
            key={tagItem.id}
            label={tagItem.name}
            permission={SEARCH_RESULT_VIEW}
            onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
              onTagClick(event, tagItem.id);
            }}
          />
        ))
      ),
    },
  ], []);

  const tableSearchResultAssetColumnList: SearchColumnItem[] = useMemo((): SearchColumnItem[] => [
    {
      id: 1,
      field: 'mainPhoto',
      label: 'Photo',
      isSortable: false,
      isNumber: false,
      isImage: true,
      component: (searchResultItem: SearchResultItem): ReactElement => (
        <Image
          isCentered
          text={searchResultItem.name}
          link={searchResultItem.mainPhoto || AssetDefaultImage}
        />
      ),
    },
    {
      id: 2,
      field: 'name',
      label: 'Name',
      isSortable: true,
      isNumber: false,
      component: (searchResultItem: SearchResultItem): ReactElement => (
        <FilteredLink withNewWindow link={`${ROUTE_ASSET}/${searchResultItem.id}`}>
          {searchResultItem.name}
        </FilteredLink>
      ),
    },
    {
      id: 3,
      field: 'portfolioName',
      label: 'Portfolio',
      isSortable: true,
      isNumber: false,
    },
    {
      id: 4,
      field: 'location',
      label: 'Address',
      isSortable: false,
      isNumber: false,
    },
    {
      id: 5,
      field: 'tags',
      label: 'Tags',
      isSortable: false,
      isMultiline: true,
      component: (searchResultItem: SearchResultItem): ReactElement => (
        searchResultItem.tags.map((tagItem: Tag): ReactElement => (
          <ChipLink
            key={tagItem.id}
            label={tagItem.name}
            permission={SEARCH_RESULT_VIEW}
            onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
              onTagClick(event, tagItem.id);
            }}
          />
        ))
      ),
    },
  ], []);

  const tableSearchResultActivityColumnList: SearchColumnItem[] = useMemo((): SearchColumnItem[] => [
    {
      id: 1,
      field: 'dealPhoto',
      label: 'Deal Photo',
      isSortable: false,
      isNumber: false,
      isImage: true,
      component: (searchResultItem: SearchResultItem): ReactElement => (
        <Image
          isCentered
          text={searchResultItem.name}
          link={searchResultItem.dealPhoto || AssetDefaultImage}
        />
      ),
    },
    {
      id: 2,
      field: 'dealName',
      label: 'Deal Name',
      isSortable: true,
      isNumber: false,
      component: (searchResultItem: SearchResultItem): ReactElement => (
        <FilteredLink withNewWindow link={`${ROUTE_ACTIVITY}/${searchResultItem.id}`}>
          {searchResultItem.dealName}
        </FilteredLink>
      ),
    },
    {
      id: 3,
      field: 'statusText',
      label: 'Status',
      isSortable: true,
      isNumber: false,
    },
    {
      id: 4,
      field: 'amount',
      label: 'Amount',
      isSortable: true,
      isNumber: true,
    },
    {
      id: 5,
      field: 'lenderText',
      label: 'Lender',
      isSortable: true,
      isNumber: false,
    },
    {
      id: 6,
      field: 'brokerText',
      label: 'Broker',
      isSortable: true,
      isNumber: false,
    },
    {
      id: 7,
      field: 'sponsorText',
      label: 'Sponsor',
      isSortable: true,
      isNumber: false,
    },
    {
      id: 8,
      field: 'tags',
      label: 'Tags',
      isSortable: false,
      isMultiline: true,
      component: (searchResultItem: SearchResultItem): ReactElement => (
        searchResultItem.tags.map((tagItem: Tag): ReactElement => (
          <ChipLink
            key={tagItem.id}
            label={tagItem.name}
            permission={SEARCH_RESULT_VIEW}
            onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
              onTagClick(event, tagItem.id);
            }}
          />
        ))
      ),
    },
  ], []);

  const tableSearchResultDealTaskColumnList: SearchColumnItem[] = useMemo((): SearchColumnItem[] => [
    {
      id: 1,
      field: 'name',
      label: 'Name',
      isSortable: true,
      isNumber: false,
      component: (searchResultItem: SearchResultItem): ReactElement => {
        const queryParams: ParsedQueryParams = {
          [QUERY_PARAM_TAB]: TAB_NAME_DUE_DILIGENCE,
          [QUERY_PARAM_DEAL_TASK_ID]: searchResultItem.id,
        };

        return (
          <FilteredLink
            withNewWindow
            link={`${ROUTE_DEAL}/${searchResultItem.dealId}${FormatService.formatQueryParams(queryParams)}`}
          >
            {searchResultItem.name}
          </FilteredLink>
        )
      },
    },
    {
      id: 2,
      field: 'parentTaskName',
      label: 'Parent Task',
      isSortable: true,
      isNumber: false,
    },
    {
      id: 3,
      field: 'groupName',
      label: 'Group',
      isSortable: true,
      isNumber: false,
    },
    {
      id: 4,
      field: 'dealName',
      label: 'Deal',
      isSortable: true,
      isNumber: false,
    },
    {
      id: 5,
      field: 'statusText',
      label: 'Status',
      isSortable: true,
      isNumber: false,
    },
    {
      id: 6,
      field: 'contactName',
      label: 'Contact',
      isSortable: true,
      isNumber: false,
    },
    {
      id: 7,
      field: 'dueDate',
      label: 'Due Date',
      isSortable: true,
      isNumber: false,
    },
  ], []);

  const onRowClick: Function = useCallback((route: string): void => {
    navigate(route);
  }, []);

  const onContactRowClick: Function = useCallback((contactId: number): void => {
    onRowClick(`${ROUTE_CONTACT}/${contactId}`);
  }, []);

  const onDealRowClick: Function = useCallback((dealId: number): void => {
    onRowClick(`${ROUTE_DEAL}/${dealId}`);
  }, []);

  const onAssetRowClick: Function = useCallback((assetId: number): void => {
    onRowClick(`${ROUTE_ASSET}/${assetId}`);
  }, []);

  const onActivityRowClick: Function = useCallback((activityId: number): void => {
    onRowClick(`${ROUTE_ACTIVITY}/${activityId}`);
  }, []);

  const onDealTaskRowClick: Function = useCallback((dealTaskId: number, dealId: number): void => {
    onRowClick(
      `${ROUTE_DEAL}/${dealId}?${QUERY_PARAM_TAB}=${TAB_NAME_DUE_DILIGENCE}`
      + `&${QUERY_PARAM_DEAL_TASK_ID}=${dealTaskId}`,
    );
  }, []);

  const onTagClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, tagId: number,
  ): void => {
    const queryParams: LocationQueryParams = {
      [QUERY_PARAM_TAG_ID]: tagId,
    };
    navigate({
      pathname: ROUTE_SEARCH,
      search: FormatService.formatQueryParams(queryParams),
    });

    event.stopPropagation();
  }, []);

  const tabs: TabItem[] = [
    {
      id: 1,
      name: 'all',
      title: 'All results',
      isVisible: true,
      component: searchResultListData && searchResultTotalData && (
        <Fragment>
          {
            Object.values(searchResultTotalData).reduce((itemsTotal: number, searchResultTotal: number): number => (
              itemsTotal + (searchResultTotal || 0)
            ), 0) ? (
              <Fragment>
                <CheckAccess permissions={contactListViewPermissions}>
                  <SearchResultGroup
                    searchResultData={searchResultListData[SEARCH_RESULT_GROUP_PEOPLE]}
                    searchResultTotal={searchResultTotalData[SEARCH_RESULT_GROUP_PEOPLE]}
                    groupName={SEARCH_RESULT_GROUP_PEOPLE}
                    headerTitle="People"
                    primaryColumnName="name"
                    secondaryColumnName="groupText"
                    tableSearchResultColumnList={tableSearchResultPersonColumnList}
                    defaultSortState={DEFAULT_SORT_CONTACT_LIST}
                    onRowClick={onContactRowClick}
                  />
                </CheckAccess>
                <CheckAccess permissions={contactListViewPermissions}>
                  <SearchResultGroup
                    searchResultData={searchResultListData[SEARCH_RESULT_GROUP_COMPANIES]}
                    searchResultTotal={searchResultTotalData[SEARCH_RESULT_GROUP_COMPANIES]}
                    groupName={SEARCH_RESULT_GROUP_COMPANIES}
                    headerTitle="Companies"
                    primaryColumnName="name"
                    secondaryColumnName="groupText"
                    tableSearchResultColumnList={tableSearchResultCompanyColumnList}
                    tableSearchResultSubGroupColumnList={tableSearchResultPersonColumnSubItemList}
                    defaultSortState={DEFAULT_SORT_CONTACT_LIST}
                    subItemsExpandedMap={companySubItemsExpandedMap}
                    onRowClick={onContactRowClick}
                  />
                </CheckAccess>
                <CheckAccess permissions={dealListViewPermissions}>
                  <SearchResultGroup
                    searchResultData={searchResultListData[SEARCH_RESULT_GROUP_DEALS]}
                    searchResultTotal={searchResultTotalData[SEARCH_RESULT_GROUP_DEALS]}
                    groupName={SEARCH_RESULT_GROUP_DEALS}
                    headerTitle="Deals"
                    primaryColumnName="name"
                    secondaryColumnName="statusText"
                    tableSearchResultColumnList={tableSearchResultDealColumnList}
                    defaultSortState={DEFAULT_SORT_DEAL_LIST}
                    onRowClick={onDealRowClick}
                  />
                  <SearchResultGroup
                    searchResultData={searchResultListData[SEARCH_RESULT_GROUP_DEALS_CLOSED]}
                    searchResultTotal={searchResultTotalData[SEARCH_RESULT_GROUP_DEALS_CLOSED]}
                    groupName={SEARCH_RESULT_GROUP_DEALS_CLOSED}
                    headerTitle="Closed Deals"
                    primaryColumnName="name"
                    secondaryColumnName="statusText"
                    tableSearchResultColumnList={tableSearchResultDealColumnList}
                    defaultSortState={DEFAULT_SORT_DEAL_LIST}
                    onRowClick={onDealRowClick}
                  />
                </CheckAccess>
                <CheckAccess permissions={assetListViewPermissions}>
                  <SearchResultGroup
                    searchResultData={searchResultListData[SEARCH_RESULT_GROUP_ASSETS]}
                    searchResultTotal={searchResultTotalData[SEARCH_RESULT_GROUP_ASSETS]}
                    groupName={SEARCH_RESULT_GROUP_ASSETS}
                    headerTitle="Assets"
                    primaryColumnName="name"
                    secondaryColumnName="portfolioName"
                    tableSearchResultColumnList={tableSearchResultAssetColumnList}
                    defaultSortState={DEFAULT_SORT_ASSET_LIST}
                    onRowClick={onAssetRowClick}
                  />
                </CheckAccess>
                <CheckAccess permissions={activityListViewPermissions}>
                  <SearchResultGroup
                    searchResultData={searchResultListData[SEARCH_RESULT_GROUP_ACTIVITIES]}
                    searchResultTotal={searchResultTotalData[SEARCH_RESULT_GROUP_ACTIVITIES]}
                    groupName={SEARCH_RESULT_GROUP_ACTIVITIES}
                    headerTitle="Activity"
                    primaryColumnName="dealName"
                    secondaryColumnName="lenderText"
                    tableSearchResultColumnList={tableSearchResultActivityColumnList}
                    defaultSortState={DEFAULT_SORT_ACTIVITY_LIST}
                    onRowClick={onActivityRowClick}
                  />
                </CheckAccess>
                <CheckAccess permissions={dealTaskListViewPermissions}>
                  <SearchResultGroup
                    searchResultData={searchResultListData[SEARCH_RESULT_GROUP_DEAL_TASKS]}
                    searchResultTotal={searchResultTotalData[SEARCH_RESULT_GROUP_DEAL_TASKS]}
                    groupName={SEARCH_RESULT_GROUP_DEAL_TASKS}
                    headerTitle="Deal Tasks"
                    primaryColumnName="name"
                    secondaryColumnName="groupText"
                    tableSearchResultColumnList={tableSearchResultDealTaskColumnList}
                    defaultSortState={DEFAULT_SORT_DEAL_TASK_LIST}
                    onRowClick={onDealTaskRowClick}
                  />
                </CheckAccess>
              </Fragment>
            ) : (
              <NoContentBlock>
                No results found by your request
              </NoContentBlock>
            )
          }
        </Fragment>
      ),
    },
    {
      id: 2,
      name: 'people',
      title: 'People',
      permission: CONTACT_LIST_VIEW,
      isVisible: searchResultListData && searchResultListData[SEARCH_RESULT_GROUP_PEOPLE]
        && !!searchResultListData[SEARCH_RESULT_GROUP_PEOPLE].length,
      component: searchResultListData && searchResultTotalData && (
        <SearchResultGroup
          searchResultData={searchResultListData[SEARCH_RESULT_GROUP_PEOPLE]}
          searchResultTotal={searchResultTotalData[SEARCH_RESULT_GROUP_PEOPLE]}
          groupName={SEARCH_RESULT_GROUP_PEOPLE}
          headerTitle="People"
          primaryColumnName="name"
          secondaryColumnName="groupText"
          tableSearchResultColumnList={tableSearchResultPersonColumnList}
          defaultSortState={DEFAULT_SORT_CONTACT_LIST}
          onRowClick={onContactRowClick}
        />
      ),
    },
    {
      id: 3,
      name: 'companies',
      title: 'Companies',
      permission: CONTACT_LIST_VIEW,
      isVisible: searchResultListData && searchResultListData[SEARCH_RESULT_GROUP_COMPANIES]
        && !!searchResultListData[SEARCH_RESULT_GROUP_COMPANIES].length,
      component: searchResultListData && searchResultTotalData && (
        <SearchResultGroup
          searchResultData={searchResultListData[SEARCH_RESULT_GROUP_COMPANIES]}
          searchResultTotal={searchResultTotalData[SEARCH_RESULT_GROUP_COMPANIES]}
          groupName={SEARCH_RESULT_GROUP_COMPANIES}
          headerTitle="Companies"
          primaryColumnName="name"
          secondaryColumnName="groupText"
          tableSearchResultColumnList={tableSearchResultCompanyColumnList}
          tableSearchResultSubGroupColumnList={tableSearchResultPersonColumnSubItemList}
          defaultSortState={DEFAULT_SORT_CONTACT_LIST}
          subItemsExpandedMap={companySubItemsExpandedMap}
          onRowClick={onContactRowClick}
        />
      ),
    },
    {
      id: 4,
      name: 'deals',
      title: 'Deals',
      permission: DEAL_LIST_VIEW,
      isVisible: searchResultListData && searchResultListData[SEARCH_RESULT_GROUP_DEALS]
        && !!searchResultListData[SEARCH_RESULT_GROUP_DEALS].length,
      component: searchResultListData && searchResultTotalData && (
        <SearchResultGroup
          searchResultData={searchResultListData[SEARCH_RESULT_GROUP_DEALS]}
          searchResultTotal={searchResultTotalData[SEARCH_RESULT_GROUP_DEALS]}
          groupName={SEARCH_RESULT_GROUP_DEALS}
          headerTitle="Deals"
          primaryColumnName="name"
          secondaryColumnName="statusText"
          tableSearchResultColumnList={tableSearchResultDealColumnList}
          defaultSortState={DEFAULT_SORT_DEAL_LIST}
          onRowClick={onDealRowClick}
        />
      ),
    },
    {
      id: 5,
      name: 'closedDeals',
      title: 'Closed Deals',
      permission: DEAL_LIST_VIEW,
      isVisible: searchResultListData && searchResultListData[SEARCH_RESULT_GROUP_DEALS_CLOSED]
        && !!searchResultListData[SEARCH_RESULT_GROUP_DEALS_CLOSED].length,
      component: searchResultListData && searchResultTotalData && (
        <SearchResultGroup
          searchResultData={searchResultListData[SEARCH_RESULT_GROUP_DEALS_CLOSED]}
          searchResultTotal={searchResultTotalData[SEARCH_RESULT_GROUP_DEALS_CLOSED]}
          groupName={SEARCH_RESULT_GROUP_DEALS_CLOSED}
          headerTitle="Closed Deals"
          primaryColumnName="name"
          secondaryColumnName="statusText"
          tableSearchResultColumnList={tableSearchResultDealColumnList}
          defaultSortState={DEFAULT_SORT_DEAL_LIST}
          onRowClick={onDealRowClick}
        />
      ),
    },
    {
      id: 6,
      name: 'assets',
      title: 'Assets',
      permission: ASSET_LIST_VIEW,
      isVisible: searchResultListData && searchResultListData[SEARCH_RESULT_GROUP_ASSETS]
        && !!searchResultListData[SEARCH_RESULT_GROUP_ASSETS].length,
      component: searchResultListData && searchResultTotalData && (
        <SearchResultGroup
          searchResultData={searchResultListData[SEARCH_RESULT_GROUP_ASSETS]}
          searchResultTotal={searchResultTotalData[SEARCH_RESULT_GROUP_ASSETS]}
          groupName={SEARCH_RESULT_GROUP_ASSETS}
          headerTitle="Assets"
          primaryColumnName="name"
          secondaryColumnName="portfolioName"
          tableSearchResultColumnList={tableSearchResultAssetColumnList}
          defaultSortState={DEFAULT_SORT_ASSET_LIST}
          onRowClick={onAssetRowClick}
        />
      ),
    },
    {
      id: 7,
      name: 'activities',
      title: 'Activities',
      permission: ACTIVITY_LIST_VIEW,
      isVisible: searchResultListData && searchResultListData[SEARCH_RESULT_GROUP_ACTIVITIES]
        && !!searchResultListData[SEARCH_RESULT_GROUP_ACTIVITIES].length,
      component: searchResultListData && searchResultTotalData && (
        <SearchResultGroup
          searchResultData={searchResultListData[SEARCH_RESULT_GROUP_ACTIVITIES]}
          searchResultTotal={searchResultTotalData[SEARCH_RESULT_GROUP_ACTIVITIES]}
          groupName={SEARCH_RESULT_GROUP_ACTIVITIES}
          headerTitle="Activity"
          primaryColumnName="dealName"
          secondaryColumnName="statusText"
          tableSearchResultColumnList={tableSearchResultActivityColumnList}
          defaultSortState={DEFAULT_SORT_ACTIVITY_LIST}
          onRowClick={onActivityRowClick}
        />
      ),
    },
    {
      id: 8,
      name: 'dealTasks',
      title: 'Deal Tasks',
      permission: DEAL_TASK_LIST_VIEW,
      isVisible: searchResultListData && searchResultListData[SEARCH_RESULT_GROUP_DEAL_TASKS]
        && !!searchResultListData[SEARCH_RESULT_GROUP_DEAL_TASKS].length,
      component: searchResultListData && searchResultTotalData && (
        <SearchResultGroup
          searchResultData={searchResultListData[SEARCH_RESULT_GROUP_DEAL_TASKS]}
          searchResultTotal={searchResultTotalData[SEARCH_RESULT_GROUP_DEAL_TASKS]}
          groupName={SEARCH_RESULT_GROUP_DEAL_TASKS}
          headerTitle="Deal Tasks"
          primaryColumnName="name"
          secondaryColumnName="groupText"
          tableSearchResultColumnList={tableSearchResultDealTaskColumnList}
          defaultSortState={DEFAULT_SORT_DEAL_TASK_LIST}
          onRowClick={onDealTaskRowClick}
        />
      ),
    },
  ];

  const setFilterValue: Function = useCallback((filterType: string, value: string): Record<string, FilterValue> => {
    const selectedValue: FilterValue = FormatService.parseInt(value) !== FILTER_VALUE_EMPTY ? value : null;
    const newFilters: Record<string, FilterValue> = { ...currentFilters, [filterType]: selectedValue };

    setCurrentFilters(newFilters);

    return newFilters;
  }, [currentFilters]);

  const onResetFilters: Function = useCallback((): void => {
    setCurrentFilters({ ...filters });
  }, [filters]);

  const onApplyFilters: Function = useCallback((newFilters: Record<string, FilterValue>): void => {
    const resultNewFilters: Record<string, FilterValue> = newFilters || currentFilters;

    setFilters({ ...resultNewFilters });

    dispatch(getSearchResultData({
      query: queryParamQuery,
      tagId,
      filters: resultNewFilters,
    }));

    const queryParams: LocationQueryParams = locationQueryParams || {};

    if (queryParamQuery) {
      queryParams[QUERY_PARAM_QUERY] = queryParamQuery;
    }

    if (searchTag) {
      queryParams[QUERY_PARAM_TAG_ID] = searchTag.id;
    }

    if (resultNewFilters) {
      Object.entries(resultNewFilters).forEach(([filterTypeItem, valueItem]: [string, FilterValue]): void => {
        const filterUrlName: string = FormatService.formatCamelCaseToKebabCase(filterTypeItem);

        if (valueItem) {
          queryParams[filterUrlName] = encodeURIComponent(valueItem);
        } else {
          delete queryParams[filterUrlName];
        }
      });
    }

    navigate({
      pathname: ROUTE_SEARCH,
      search: FormatService.formatQueryParams(queryParams),
    });
  }, [locationQueryParams, currentFilters, tagId, queryParamQuery, searchTag]);

  const onDeleteFilterValue: Function = useCallback((filterType: string): void => {
    const newFilters: Record<string, FilterValue> = setFilterValue(filterType, null);

    onApplyFilters(newFilters);
  }, [locationQueryParams, currentFilters, tagId, queryParamQuery, searchTag]);

  const applyUrlFilters: Function = useCallback((): Record<string, FilterValue> => {
    const filterValues: Record<string, FilterValue> = { ...filters };

    if (queryFilterDealType) {
      filterValues[FILTER_TYPE_DEAL_TYPE] = queryFilterDealType;
    }

    if (queryFilterLocation) {
      filterValues[FILTER_TYPE_LOCATION] = queryFilterLocation;
    }

    if (queryFilterAssetType) {
      filterValues[FILTER_TYPE_ASSET_TYPE] = queryFilterAssetType;
    }

    if (queryFilterDealSize) {
      filterValues[FILTER_TYPE_DEAL_SIZE] = queryFilterDealSize;
    }

    setFilters(filterValues);

    return filterValues;
  }, [filters, queryFilterDealType, queryFilterLocation, queryFilterAssetType, queryFilterDealSize]);

  const onTabChange: Function = useCallback((newTabId: number): void => {
    const queryParams: LocationQueryParams = locationQueryParams || {};

    tabs.forEach((tabItem: TabItem): void => {
      if (tabItem.id === newTabId) {
        queryParams[QUERY_PARAM_TAB] = tabItem.name;

        if (queryParamQuery) {
          queryParams[QUERY_PARAM_QUERY] = queryParamQuery;
        }

        if (filters) {
          Object.entries(filters).forEach(([filterTypeItem, valueItem]: [string, FilterValue]): void => {
            const filterUrlName: string = FormatService.formatCamelCaseToKebabCase(filterTypeItem);

            if (valueItem) {
              queryParams[filterUrlName] = encodeURIComponent(valueItem);
            } else {
              delete queryParams[filterUrlName];
            }
          });
        }

        navigate({
          pathname: ROUTE_SEARCH,
          search: FormatService.formatQueryParams(queryParams),
        });
      }
    });
  }, [locationQueryParams, tabs, queryParamQuery]);

  const onRefresh: Function = useCallback((): void => {
    const filterValues: Record<string, FilterValue> = applyUrlFilters();

    dispatch(getSearchResultData({
      query: queryParamQuery,
      tagId,
      filters: filterValues,
    }));
  }, [queryParamQuery, tagId]);

  useEffect((): void => {
    if (tabIndex === null) {
      setTabIndex(TAB_INDEX_DEFAULT);
    }
  }, [tabIndex]);

  useEffect((): void => {
    if (searchResultData) {
      const initialListData: Record<string, SearchResultGroupList> = {};
      const initialTotalData: Record<string, number> = {};
      searchResultData.forEach((searchResultGroup: SearchResultGroupItem): void => {
        initialListData[searchResultGroup.name] = searchResultGroup[SEARCH_RESULT_GROUP_KEY_MAP[searchResultGroup.name]]
          || [];
        initialTotalData[searchResultGroup.name] = searchResultGroup.total;
      });
      setSearchResultListData(initialListData);
      setSearchResultTotalData(initialTotalData);
    }
  }, [searchResultData]);

  useEffect((): void => {
    if (queryParamQuery || tagId) {
      const filterValues: Record<string, FilterValue> = applyUrlFilters();

      dispatch(resetSearchResultData());
      dispatch(getSearchResultData({
        query: queryParamQuery,
        tagId,
        filters: filterValues,
      }));
    }
  }, [queryParamQuery, tagId]);

  if ((queryParamQuery || tagId) && !isDataLoaded) {
    return <Loader isPageLoader />;
  }

  return (
    <CheckAccess permissions={searchResultViewPermissions}>
      <PullToRefresh onRefresh={onRefresh}>
        <ContentHeader title={pageTitle} />
        {
          isMobileScreen && (
            <SearchBar isPlain />
          )
        }
        {
          searchCriteria && searchResultData && (
            <PageTabs
              isCompressed={isRightDrawerOpen}
              tabs={tabs.filter((tabItem: TabItem): boolean => !!tabItem.isVisible)}
              currentTab={queryTab}
              subComponent={(
                <div>
                  <Controls
                    withNoEditMode
                    withFilters
                    withAllFiltersEmpty={withAllFiltersEmpty}
                    currentFilters={currentFilters}
                    filters={filters}
                    onApplyFilters={onApplyFilters}
                    onResetFilters={onResetFilters}
                    filterComponent={(
                      <SearchFilters
                        currentFilters={currentFilters}
                        filters={filters}
                        onSetFilterValue={setFilterValue}
                        filterDealTypeList={filterDealTypeList}
                        filterDealSizeList={filterDealSizeList}
                        filterLocationList={filterLocationList}
                        filterAssetTypeList={filterAssetTypeList}
                      />
                    )}
                  />
                  <FilterPanel
                    filters={filters}
                    filterOptionMap={FILTER_OPTION_MAP}
                    filterKeyMap={FILTER_KEY_MAP}
                    filterLabelMap={FILTER_LABEL_MAP}
                    filterLabelKeyMap={FILTER_LABEL_KEY_MAP}
                    onDeleteFilterValue={onDeleteFilterValue}
                  />
                </div>
              )}
              onSetTabIndex={setTabIndex}
              onTabChange={onTabChange}
            />
          )
        }
      </PullToRefresh>
    </CheckAccess>
  );
});

const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    search,
    rightDrawer,
  } = reducers;

  return {
    isDataLoaded: !!search.searchResultData,
    isRightDrawerOpen: rightDrawer.isDrawerOpen,
    searchTag: search.searchResultData && search.searchResultData.searchTag,
    searchResultData: search.searchResultData && search.searchResultData.searchResult,
    filterDealTypeList: search.searchResultData && search.searchResultData.searchResultFilters.dealTypeList,
    filterLocationList: search.searchResultData && search.searchResultData.searchResultFilters.locationList,
    filterAssetTypeList: search.searchResultData && search.searchResultData.searchResultFilters.assetTypeList,
    filterDealSizeList: search.searchResultData && search.searchResultData.searchResultFilters.dealSizeList,
  };
};

export default connect(mapState)(Search);
